/**
 * Signal Registry
 * 
 * Frontend mirror of the backend signal registry.
 * Defines all available signals and their parameter schemas.
 * 
 * Backend reference: rust-backend/crates/backtest/src/signals/registry.rs
 */

import { SignalSchema } from "./types";

/**
 * Position-data-based signals that require leg_idx when used as entry signals.
 * These signals read position P&L data, so when used for entry they must reference
 * another leg (the position doesn't exist yet for the current leg at entry time).
 */
export const POSITION_DATA_SIGNALS = new Set([
  "dollar_return_cap",
  "percentage_return_cap",
  "dte_signal",
]);

// Weekday options for scheduled exit
const WEEKDAY_OPTIONS = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
];

/**
 * Signal Registry
 * 
 * Each signal definition includes:
 * - key: Unique identifier matching backend
 * - label: Human-readable name
 * - description: Explains what the signal does
 * - category: "entry", "exit", or "both"
 * - params: Parameter definitions with types, labels, and validation
 */
export const SIGNAL_REGISTRY: Record<string, SignalSchema> = {
  // ============ ENTRY SIGNALS ============
  on_exit: {
    key: "on_exit",
    label: "Period Start/ On Exit",
    description: "As an entry signal, opens a new position at the start of each period. As an exit signal, closes the position whenever another leg's exit is triggered. In intraday mode, this evaluates true on every tick.",
    category: "both",
    params: {},
  },

  ytd_return_cap: {
    key: "ytd_return_cap",
    label: "YTD Return Cap",
    description: "Triggers when year-to-date return hits upper or lower threshold. For example, enter when SPY is up 15% YTD or down 10% YTD.",
    category: "both",
    params: {
      symbol: {
        type: "symbol",
        label: "Symbol",
        required: false,
        placeholder: "Search symbol or company...",
        description: "Stock symbol to track (defaults to strategy symbol)",
      },
      upper_limit: {
        type: "percent",
        label: "Upper Limit",
        required: false,
        placeholder: "e.g., 15",
        description: "Enter when YTD return exceeds this percentage",
      },
      lower_limit: {
        type: "percent",
        label: "Lower Limit",
        required: false,
        placeholder: "e.g., -10",
        description: "Enter when YTD return drops below this percentage",
      },
    },
    validate: (params) => {
      if (params.upper_limit === undefined && params.lower_limit === undefined) {
        return "At least one of Upper Limit or Lower Limit is required";
      }
      return true;
    },
  },

  n_day_return_cap: {
    key: "n_day_return_cap",
    label: "N-Day Return Cap",
    description: "Triggers when the return over N trading days hits a threshold. For example, enter when the 5-day return exceeds 3% or drops below -2%.",
    category: "both",
    params: {
      symbol: {
        type: "symbol",
        label: "Symbol",
        required: false,
        placeholder: "Search symbol or company...",
        description: "Stock symbol to track (defaults to strategy symbol)",
      },
      n: {
        type: "integer",
        label: "Days (N)",
        required: false,
        min: 1,
        placeholder: "e.g., 5",
        description: "Number of trading days for return calculation (default: 1)",
      },
      upper_limit: {
        type: "percent",
        label: "Upper Limit",
        required: false,
        placeholder: "e.g., 5",
        description: "Enter when N-day return exceeds this percentage",
      },
      lower_limit: {
        type: "percent",
        label: "Lower Limit",
        required: false,
        placeholder: "e.g., -3",
        description: "Enter when N-day return drops below this percentage",
      },
    },
    validate: (params) => {
      if (params.upper_limit === undefined && params.lower_limit === undefined) {
        return "At least one of Upper Limit or Lower Limit is required";
      }
      return true;
    },
  },

  n_day_before_earnings: {
    key: "n_day_before_earnings",
    label: "N-Day Before Earnings",
    description: "Triggers on each trading day in the N-day window before an earnings date. For example, with N=3 it fires on the three trading sessions immediately before earnings day.",
    category: "both",
    params: {
      symbol: {
        type: "symbol",
        label: "Symbol",
        required: false,
        placeholder: "Search symbol or company...",
        description: "Stock symbol whose earnings calendar will be used (defaults to strategy symbol)",
      },
      days_before: {
        type: "integer",
        label: "Days Before",
        required: true,
        min: 1,
        placeholder: "e.g., 3",
        description: "Number of trading days before each earnings date to trigger",
      },
    },
  },

  ma_crossover: {
    key: "ma_crossover",
    label: "MA Crossover",
    description: "Triggers on moving average crossover. For example, enter when the 50-day MA crosses above the 200-day MA (golden cross) or below it (death cross).",
    category: "both",
    params: {
      symbol: {
        type: "symbol",
        label: "Symbol",
        required: false,
        placeholder: "Search symbol or company...",
        description: "Stock symbol to track (defaults to strategy symbol)",
      },
      short_window: {
        type: "integer",
        label: "Short Window",
        required: true,
        min: 1,
        placeholder: "e.g., 50",
        description: "Number of trading days for the short moving average",
      },
      long_window: {
        type: "integer",
        label: "Long Window",
        required: true,
        min: 1,
        placeholder: "e.g., 200",
        description: "Number of trading days for the long moving average",
      },
      trend: {
        type: "select",
        label: "Trend",
        required: true,
        options: [
          { value: "upward", label: "Upward" },
          { value: "downward", label: "Downward" },
        ],
        description: "Upward: short MA > long MA, Downward: short MA < long MA",
      },
    },
    validate: (params) => {
      const shortWindow = Number(params.short_window);
      const longWindow = Number(params.long_window);
      if (!isNaN(shortWindow) && !isNaN(longWindow) && shortWindow >= longWindow) {
        return "Short Window must be strictly less than Long Window";
      }
      return true;
    },
  },

  intraday_ma_crossover: {
    key: "intraday_ma_crossover",
    label: "MA Crossover",
    description: "Triggers on intraday moving average crossover. Uses the selected frequency unit for short and long windows (frequency every is fixed to 1).",
    category: "both",
    params: {
      symbol: {
        type: "symbol",
        label: "Symbol",
        required: false,
        placeholder: "Search symbol or company...",
        description: "Stock symbol to track (defaults to strategy symbol)",
      },
      short_window: {
        type: "integer",
        label: "Short Window",
        required: true,
        min: 1,
        placeholder: "e.g., 5",
        description: "Short moving average window in selected frequency units",
      },
      long_window: {
        type: "integer",
        label: "Long Window",
        required: true,
        min: 1,
        placeholder: "e.g., 10",
        description: "Long moving average window in selected frequency units",
      },
      trend: {
        type: "select",
        label: "Trend",
        required: true,
        options: [
          { value: "upward", label: "Upward" },
          { value: "downward", label: "Downward" },
        ],
        description: "Upward: short MA > long MA, Downward: short MA < long MA",
      },
      frequency: {
        type: "frequency",
        label: "Frequency",
        required: true,
        description: "How often to evaluate the crossover (every N seconds/minutes/hours)",
      },
    },
    validate: (params) => {
      const shortWindow = Number(params.short_window);
      const longWindow = Number(params.long_window);
      if (!isNaN(shortWindow) && !isNaN(longWindow) && shortWindow >= longWindow) {
        return "Short Window must be strictly less than Long Window";
      }

      const frequency = params.frequency as { every?: unknown; unit?: string } | undefined;
      if (
        frequency &&
        frequency.every !== undefined &&
        Number(frequency.every) !== 1
      ) {
        return "Intraday MA Crossover requires frequency every to be 1";
      }

      const unit = frequency?.unit;
      const maxByUnit: Record<string, number> = {
        hours: 6,
        minutes: 300,
        seconds: 1800,
      };
      if (unit && maxByUnit[unit] !== undefined) {
        const maxAllowed = maxByUnit[unit];
        if (!isNaN(shortWindow) && shortWindow > maxAllowed) {
          return `Short Window cannot exceed ${maxAllowed} for ${unit} frequency`;
        }
        if (!isNaN(longWindow) && longWindow > maxAllowed) {
          return `Long Window cannot exceed ${maxAllowed} for ${unit} frequency`;
        }
      }

      return true;
    },
  },

  on_schedule: {
    key: "on_schedule",
    label: "On Schedule",
    description: "Fires at the configured cadence (every N seconds/minutes/hours) during the intraday session.",
    category: "both",
    params: {
      frequency: {
        type: "frequency",
        label: "Frequency",
        required: true,
        description: "Cadence at which this signal fires (every N seconds/minutes/hours)",
      },
    },
  },

  // ============ EXIT SIGNALS ============
  scheduled_exit: {
    key: "scheduled_exit",
    label: "Scheduled Exit",
    description: "Closes the position on a fixed recurring schedule. Configure by days, weeks, months, years, or a specific weekday. For example, exit every 30 days or every Friday. At least one schedule parameter is required.",
    category: "both",
    params: {
      days: {
        type: "integer",
        label: "Days",
        required: false,
        min: 1,
        placeholder: "e.g., 30",
        description: "Exit every N days",
      },
      weeks: {
        type: "integer",
        label: "Weeks",
        required: false,
        min: 1,
        placeholder: "e.g., 4",
        description: "Exit every N weeks",
      },
      months: {
        type: "integer",
        label: "Months",
        required: false,
        min: 1,
        placeholder: "e.g., 1",
        description: "Exit every N months",
      },
      years: {
        type: "integer",
        label: "Years",
        required: false,
        min: 1,
        placeholder: "e.g., 1",
        description: "Exit every N years",
      },
      weekday: {
        type: "select",
        label: "Weekday",
        required: false,
        options: WEEKDAY_OPTIONS,
        description: "Exit on specific weekday",
      },
    },
    validate: (params) => {
      const hasSchedule = params.days || params.weeks || params.months || params.years || params.weekday;
      if (!hasSchedule) {
        return "At least one schedule parameter is required";
      }
      return true;
    },
  },

  dollar_return_cap: {
    key: "dollar_return_cap",
    label: "Dollar Return Cap",
    description: "Exits when profit or loss reaches a dollar amount. For example, take profit at $1,000 gain or stop loss at $500 loss.",
    category: "both",
    params: {
      upper_limit: {
        type: "dollar",
        label: "Take Profit",
        required: false,
        placeholder: "e.g., 1000",
        description: "Exit when profit reaches this amount",
      },
      lower_limit: {
        type: "dollar",
        label: "Stop Loss",
        required: false,
        placeholder: "e.g., -500",
        description: "Exit when loss reaches this amount",
      },
      leg_idx: {
        type: "integer",
        label: "Leg Number",
        required: false,
        min: 1,
        placeholder: "1",
        description: "Which leg to track (1 = first leg)",
      },
      frequency: {
        type: "frequency",
        label: "Frequency",
        required: false,
        description: "Cadence used to evaluate the cap in intraday mode (every N seconds/minutes/hours)",
      },
    },
    validate: (params) => {
      if (params.upper_limit === undefined && params.lower_limit === undefined) {
        return "At least one of Take Profit or Stop Loss is required";
      }
      return true;
    },
  },

  percentage_return_cap: {
    key: "percentage_return_cap",
    label: "Percentage Return Cap",
    description: "Exits when return percentage reaches a threshold. For example, take profit at +50% return or stop loss at -20%.",
    category: "both",
    params: {
      upper_limit: {
        type: "percent",
        label: "Take Profit %",
        required: false,
        placeholder: "e.g., 50",
        description: "Exit when return exceeds this percentage",
      },
      lower_limit: {
        type: "percent",
        label: "Stop Loss %",
        required: false,
        placeholder: "e.g., -20",
        description: "Exit when return drops below this percentage",
      },
      leg_idx: {
        type: "integer",
        label: "Leg Number",
        required: false,
        min: 1,
        placeholder: "1",
        description: "Which leg to track (1 = first leg)",
      },
      frequency: {
        type: "frequency",
        label: "Frequency",
        required: false,
        description: "Cadence used to evaluate the cap in intraday mode (every N seconds/minutes/hours)",
      },
    },
    validate: (params) => {
      if (params.upper_limit === undefined && params.lower_limit === undefined) {
        return "At least one of Take Profit % or Stop Loss % is required";
      }
      return true;
    },
    validateParam: (paramKey: string, value: unknown) => {
      if (paramKey === "upper_limit" && value !== undefined) {
        const numValue = typeof value === "number" ? value : parseFloat(value as string);
        if (!isNaN(numValue) && numValue <= 0) {
          return "Profit cap must be positive";
        }
      }
      if (paramKey === "lower_limit" && value !== undefined) {
        const numValue = typeof value === "number" ? value : parseFloat(value as string);
        if (!isNaN(numValue) && numValue >= 0) {
          return "Stop loss must be negative";
        }
      }
      return true;
    },
  },

  dte_signal: {
    key: "dte_signal",
    label: "Days to Expiration",
    description: "Exits when days to expiration is at or below a threshold. For example, close an options position when DTE drops to 21 days to avoid time decay.",
    category: "both",
    params: {
      dte_threshold: {
        type: "integer",
        label: "DTE Threshold",
        required: true,
        min: 0,
        placeholder: "e.g., 21",
        description: "Exit when DTE is at or below this value",
      },
      leg_idx: {
        type: "integer",
        label: "Leg Number",
        required: false,
        min: 1,
        placeholder: "1",
        description: "Which leg to track (1 = first leg)",
      },
    },
  },

  rsi_signal: {
    key: "rsi_signal",
    label: "RSI Signal",
    description: "Triggers when RSI crosses above or below a threshold. For example, buy when RSI drops below 30 (oversold) or sell when RSI rises above 70 (overbought).",
    category: "both",
    params: {
      symbol: {
        type: "symbol",
        label: "Symbol",
        required: false,
        placeholder: "Search symbol or company...",
        description: "Stock symbol to track (defaults to strategy symbol)",
      },
      period: {
        type: "integer",
        label: "RSI Period",
        required: false,
        min: 1,
        placeholder: "e.g., 14",
        description: "Number of trading days for RSI calculation (default: 14)",
      },
      upper_limit: {
        type: "number",
        label: "Overbought Level",
        required: false,
        min: 0,
        max: 100,
        placeholder: "e.g., 70",
        description: "Trigger when RSI is at or above this value",
      },
      lower_limit: {
        type: "number",
        label: "Oversold Level",
        required: false,
        min: 0,
        max: 100,
        placeholder: "e.g., 30",
        description: "Trigger when RSI is at or below this value",
      },
    },
    validate: (params) => {
      if (params.upper_limit === undefined && params.lower_limit === undefined) {
        return "At least one of Overbought Level or Oversold Level is required";
      }
      return true;
    },
  },

  bollinger_bands: {
    key: "bollinger_bands",
    label: "Bollinger Bands",
    description: "Triggers when price crosses Bollinger Band boundaries. For example, buy when price drops below the lower band (mean reversion) or sell when it breaks above the upper band.",
    category: "both",
    params: {
      symbol: {
        type: "symbol",
        label: "Symbol",
        required: false,
        placeholder: "Search symbol or company...",
        description: "Stock symbol to track (defaults to strategy symbol)",
      },
      period: {
        type: "integer",
        label: "Period",
        required: false,
        min: 1,
        placeholder: "e.g., 20",
        description: "Number of trading days for SMA calculation (default: 20)",
      },
      num_std: {
        type: "number",
        label: "Std Deviations",
        required: false,
        min: 0.1,
        placeholder: "e.g., 2.0",
        description: "Number of standard deviations for the bands (default: 2.0)",
      },
      trigger: {
        type: "select",
        label: "Trigger",
        required: true,
        options: [
          { value: "above_upper", label: "Price crosses above upper band" },
          { value: "below_lower", label: "Price crosses below lower band" },
          { value: "cross_middle", label: "Price crosses above middle band" },
        ],
        description: "Which band crossing triggers the signal",
      },
    },
  },

  volume_spike: {
    key: "volume_spike",
    label: "Volume Spike",
    description: "Triggers when volume exceeds a multiple of its moving average. For example, enter when today's volume is 2x the 20-day average, signaling unusual activity.",
    category: "both",
    params: {
      symbol: {
        type: "symbol",
        label: "Symbol",
        required: false,
        placeholder: "Search symbol or company...",
        description: "Stock symbol to track (defaults to strategy symbol)",
      },
      period: {
        type: "integer",
        label: "Lookback Period",
        required: false,
        min: 1,
        placeholder: "e.g., 20",
        description: "Number of trading days for average volume calculation (default: 20)",
      },
      multiplier: {
        type: "number",
        label: "Volume Multiplier",
        required: false,
        min: 0.1,
        placeholder: "e.g., 2.0",
        description: "Trigger when volume exceeds this multiple of the average (default: 2.0)",
      },
    },
  },

  mean_reversion: {
    key: "mean_reversion",
    label: "Mean Reversion (Z-Score)",
    description: "Triggers when price Z-score exceeds a threshold, indicating deviation from the mean. For example, buy when Z-score drops below -2 (price is 2 std devs cheap) or sell above +2.",
    category: "both",
    params: {
      symbol: {
        type: "symbol",
        label: "Symbol",
        required: false,
        placeholder: "Search symbol or company...",
        description: "Stock symbol to track (defaults to strategy symbol)",
      },
      period: {
        type: "integer",
        label: "Lookback Period",
        required: false,
        min: 2,
        placeholder: "e.g., 20",
        description: "Number of trading days for rolling mean and std dev (default: 20)",
      },
      upper_limit: {
        type: "number",
        label: "Upper Z-Score",
        required: false,
        placeholder: "e.g., 2.0",
        description: "Trigger when Z-score is at or above this value (e.g., 2.0 = 2 std devs above mean)",
      },
      lower_limit: {
        type: "number",
        label: "Lower Z-Score",
        required: false,
        placeholder: "e.g., -2.0",
        description: "Trigger when Z-score is at or below this value (e.g., -2.0 = 2 std devs below mean)",
      },
    },
    validate: (params) => {
      if (params.upper_limit === undefined && params.lower_limit === undefined) {
        return "At least one of Upper Z-Score or Lower Z-Score is required";
      }
      return true;
    },
  },

  daily_open_close: {
    key: "daily_open_close",
    label: "Daily Open/Close",
    description: "Triggers every trading day. Useful for daily open/close strategies like selling a put at open and buying it back at close.",
    category: "both",
    params: {
      symbol: {
        type: "symbol",
        label: "Symbol",
        required: false,
        placeholder: "Search symbol or company...",
        description: "Stock symbol (used to determine valid trading days, defaults to strategy symbol)",
      },
    },
  },

  consecutive_return_cap: {
    key: "consecutive_return_cap",
    label: "Consecutive Return Cap",
    description: "Triggers when a stock has N consecutive days of returns meeting a threshold. For example, enter after 3 consecutive days where each day's return is above 1%.",
    category: "both",
    params: {
      symbol: {
        type: "symbol",
        label: "Symbol",
        required: false,
        placeholder: "Search symbol or company...",
        description: "Stock symbol to track (defaults to strategy symbol)",
      },
      n: {
        type: "integer",
        label: "Return Period (N)",
        required: false,
        min: 1,
        placeholder: "e.g., 1",
        description: "Number of trading days for each return calculation (default: 1)",
      },
      consecutive_days: {
        type: "integer",
        label: "Consecutive Days",
        required: true,
        min: 1,
        placeholder: "e.g., 3",
        description: "How many consecutive days of qualifying returns to trigger",
      },
      upper_limit: {
        type: "percent",
        label: "Upper Limit",
        required: false,
        placeholder: "e.g., 2",
        description: "Each day's return must be at or above this % to count toward the streak",
      },
      lower_limit: {
        type: "percent",
        label: "Lower Limit",
        required: false,
        placeholder: "e.g., -1",
        description: "Each day's return must be at or below this % to count toward the streak",
      },
    },
    validate: (params) => {
      if (params.upper_limit === undefined && params.lower_limit === undefined) {
        return "At least one of Upper Limit or Lower Limit is required";
      }
      return true;
    },
  },
};
